import React from 'react';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicyView = ({ setCurrentView }) => {
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 md:p-8">
        <button
          onClick={() => setCurrentView('login')}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-600 mb-6">Last Updated: November 17, 2025</p>

        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              Welcome to RepConnect. This Privacy Policy explains how RepConnect, LLC ("we", "us",
              or "our") collects, uses, discloses, and protects your personal information when you
              use our healthcare meeting scheduling platform at meetwithreps.com (the "Service").
            </p>
            <p className="mt-2">
              We are committed to protecting your privacy and handling your data in an open and
              transparent manner. By using RepConnect, you consent to the data practices described
              in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.1 Information You Provide</h3>
            <p>We collect information that you voluntarily provide when using our Service:</p>

            <div className="mt-3">
              <p className="font-semibold">Account Information:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>Name (first and last name)</li>
                <li>Email address</li>
                <li>Password (encrypted)</li>
                <li>Phone number</li>
                <li>Professional title and specialty</li>
                <li>Company/organization name</li>
                <li>Geographic location and territory</li>
                <li>License number (for healthcare providers)</li>
                <li>NPI number (National Provider Identifier)</li>
              </ul>
            </div>

            <div className="mt-3">
              <p className="font-semibold">Profile Information:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>Professional bio and credentials</li>
                <li>Dining preferences and dietary restrictions</li>
                <li>Availability schedule</li>
                <li>Contact preferences</li>
                <li>Product areas of interest</li>
              </ul>
            </div>

            <div className="mt-3">
              <p className="font-semibold">Meeting Information:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>Meeting dates, times, and locations</li>
                <li>Restaurant and meal selections</li>
                <li>Meeting notes and topics discussed</li>
                <li>Attendee information</li>
                <li>Meal costs and compliance data</li>
              </ul>
            </div>

            <div className="mt-3">
              <p className="font-semibold">Payment Information:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>Billing name and address</li>
                <li>Payment method details (processed and stored securely by Stripe)</li>
                <li>Transaction history</li>
              </ul>
            </div>

            <div className="mt-3">
              <p className="font-semibold">Communications:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>Messages sent through the platform</li>
                <li>Customer support inquiries and correspondence</li>
                <li>Feedback and survey responses</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.2 Information Automatically Collected</h3>
            <p>When you use RepConnect, we automatically collect certain information:</p>

            <div className="mt-3">
              <p className="font-semibold">Usage Data:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>Pages visited and features used</li>
                <li>Time spent on the platform</li>
                <li>Click patterns and navigation paths</li>
                <li>Search queries and filters applied</li>
              </ul>
            </div>

            <div className="mt-3">
              <p className="font-semibold">Device and Browser Information:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Device type (mobile, tablet, desktop)</li>
                <li>Screen resolution</li>
                <li>Referring website</li>
              </ul>
            </div>

            <div className="mt-3">
              <p className="font-semibold">Cookies and Tracking Technologies:</p>
              <ul className="list-disc ml-6 mt-1 space-y-1">
                <li>Session cookies to maintain your logged-in state</li>
                <li>Preference cookies to remember your settings</li>
                <li>Analytics cookies to understand usage patterns</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">2.3 Information from Third Parties</h3>
            <p>We may receive information from third-party sources:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>NPI Registry data for healthcare provider verification</li>
              <li>Company databases for sales representative verification</li>
              <li>Payment processors (Stripe) for transaction information</li>
              <li>Authentication services for account verification</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.1 Provide and Maintain the Service</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Create and manage your account</li>
              <li>Facilitate meeting scheduling and coordination</li>
              <li>Process payments and manage subscriptions</li>
              <li>Enable communication between users</li>
              <li>Provide customer support</li>
              <li>Send transactional emails and notifications</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.2 Improve and Optimize the Service</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Analyze usage patterns and trends</li>
              <li>Identify and fix technical issues</li>
              <li>Develop new features and functionality</li>
              <li>Conduct research and analytics</li>
              <li>Optimize user experience and performance</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.3 Compliance and Safety</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Verify user identities and credentials</li>
              <li>Maintain compliance records and audit trails</li>
              <li>Detect and prevent fraud and abuse</li>
              <li>Enforce our Terms of Service</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.4 Marketing and Communications</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Send promotional emails about new features (with opt-out)</li>
              <li>Provide product updates and announcements</li>
              <li>Conduct surveys and request feedback</li>
              <li>Share relevant industry news and tips</li>
            </ul>
            <p className="mt-2 text-sm">
              You may opt out of marketing communications at any time by clicking "unsubscribe"
              in our emails or adjusting your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. How We Share Your Information</h2>
            <p>We do not sell your personal information. We share your information only in the following circumstances:</p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.1 With Other Users</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Your profile information is visible to other users to facilitate connections</li>
              <li>Meeting participants can see contact information of other attendees</li>
              <li>Sales representatives can see provider preferences and availability</li>
              <li>Healthcare providers can see sales representative company and contact details</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.2 With Service Providers</h3>
            <p>We share information with trusted third-party service providers who help us operate the Service:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Supabase:</strong> Cloud database and authentication services</li>
              <li><strong>Stripe:</strong> Payment processing and subscription management</li>
              <li><strong>Vercel:</strong> Application hosting and deployment</li>
              <li><strong>Email service providers:</strong> Transactional and marketing emails</li>
              <li><strong>Analytics providers:</strong> Usage tracking and performance monitoring</li>
            </ul>
            <p className="mt-2">
              These service providers are contractually obligated to protect your information and
              use it only for the purposes we specify.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.3 For Legal Reasons</h3>
            <p>We may disclose your information if required by law or in response to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Court orders, subpoenas, or legal processes</li>
              <li>Government or regulatory requests</li>
              <li>Enforcement of our Terms of Service</li>
              <li>Protection of our rights, property, or safety</li>
              <li>Prevention of fraud or illegal activity</li>
              <li>Emergency situations involving danger to persons</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.4 Business Transfers</h3>
            <p>
              If RepConnect is involved in a merger, acquisition, sale of assets, or bankruptcy,
              your information may be transferred as part of that transaction. We will notify you
              of any such change in ownership or control of your personal information.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">4.5 With Your Consent</h3>
            <p>
              We may share your information for other purposes with your explicit consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Encryption:</strong> All data transmitted to and from RepConnect is encrypted using SSL/TLS</li>
              <li><strong>Password Security:</strong> Passwords are hashed using bcrypt before storage</li>
              <li><strong>Access Controls:</strong> Strict access controls limit who can view your data</li>
              <li><strong>Secure Infrastructure:</strong> We use secure cloud providers with SOC 2 compliance</li>
              <li><strong>Regular Security Audits:</strong> We conduct regular security assessments</li>
              <li><strong>Data Backup:</strong> Regular backups to prevent data loss</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission over the internet or electronic storage is 100%
              secure. While we strive to protect your personal information, we cannot guarantee
              absolute security. You are responsible for maintaining the confidentiality of your
              account credentials.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide the Service
              and fulfill the purposes described in this Privacy Policy, unless a longer retention
              period is required or permitted by law.
            </p>
            <p className="mt-2">
              Specifically:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Active Accounts:</strong> Data is retained while your account is active</li>
              <li><strong>Deleted Accounts:</strong> Most data is deleted within 30 days of account deletion</li>
              <li><strong>Compliance Records:</strong> Meeting and payment records may be retained for 7 years to comply with healthcare regulations</li>
              <li><strong>Legal Holds:</strong> Data subject to legal holds or investigations is retained as required</li>
              <li><strong>Backup Data:</strong> Data in backups is retained according to our backup retention schedule (typically 90 days)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Your Privacy Rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information:
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.1 Access and Portability</h3>
            <p>
              You have the right to access your personal information and request a copy in a
              portable format. You can view most of your information in your account settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.2 Correction</h3>
            <p>
              You have the right to correct inaccurate or incomplete personal information. You can
              update most information directly in your account settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.3 Deletion</h3>
            <p>
              You have the right to request deletion of your personal information, subject to certain
              exceptions (e.g., compliance requirements, legal obligations). You can delete your
              account in settings or contact us for assistance.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.4 Objection and Restriction</h3>
            <p>
              You have the right to object to or restrict certain processing of your personal
              information, such as for marketing purposes.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.5 Withdraw Consent</h3>
            <p>
              Where we process your information based on consent, you have the right to withdraw
              that consent at any time.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.6 How to Exercise Your Rights</h3>
            <p>
              To exercise any of these rights, please:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Log in to your account and update your settings</li>
              <li>Email us at privacy@meetwithreps.com</li>
              <li>Contact customer support at support@meetwithreps.com</li>
            </ul>
            <p className="mt-2">
              We will respond to your request within 30 days. We may ask you to verify your identity
              before processing your request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. Cookies and Tracking Technologies</h2>
            <p>
              RepConnect uses cookies and similar tracking technologies to enhance your experience:
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">8.1 Types of Cookies We Use</h3>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security)</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service</li>
              <li><strong>Performance Cookies:</strong> Monitor and improve Service performance</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">8.2 Managing Cookies</h3>
            <p>
              Most web browsers allow you to control cookies through settings. You can:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Block all cookies</li>
              <li>Accept only certain cookies</li>
              <li>Delete cookies after each session</li>
            </ul>
            <p className="mt-2">
              Note that blocking or deleting cookies may impact your ability to use certain features
              of RepConnect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Third-Party Links and Services</h2>
            <p>
              RepConnect may contain links to third-party websites, services, or integrations. This
              Privacy Policy does not apply to those third parties. We are not responsible for the
              privacy practices of third parties. We encourage you to read their privacy policies
              before providing any personal information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Children's Privacy</h2>
            <p>
              RepConnect is not intended for use by individuals under the age of 18. We do not
              knowingly collect personal information from children. If we become aware that we have
              collected personal information from a child without parental consent, we will take
              steps to delete that information.
            </p>
            <p className="mt-2">
              If you believe we have collected information from a child, please contact us immediately
              at privacy@meetwithreps.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. International Data Transfers</h2>
            <p>
              RepConnect is operated in the United States. If you are accessing the Service from
              outside the United States, your information may be transferred to, stored, and processed
              in the United States and other countries where our service providers operate.
            </p>
            <p className="mt-2">
              These countries may have different data protection laws than your country. By using
              RepConnect, you consent to the transfer of your information to the United States and
              other countries.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. California Privacy Rights</h2>
            <p>
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Right to know what personal information is collected, used, shared, or sold</li>
              <li>Right to delete personal information (subject to exceptions)</li>
              <li>Right to opt-out of the sale of personal information (we do not sell your information)</li>
              <li>Right to non-discrimination for exercising your rights</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us at privacy@meetwithreps.com. We will verify your
              identity before processing your request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. GDPR Rights (European Users)</h2>
            <p>
              If you are located in the European Economic Area (EEA), UK, or Switzerland, you have
              additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Rights related to automated decision-making</li>
            </ul>
            <p className="mt-2">
              You also have the right to lodge a complaint with your local data protection authority.
            </p>
            <p className="mt-2">
              Our legal basis for processing your data includes:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Contract:</strong> Processing necessary to provide the Service</li>
              <li><strong>Consent:</strong> Where you have given explicit consent</li>
              <li><strong>Legitimate Interests:</strong> For fraud prevention, security, and service improvement</li>
              <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">14. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices,
              technology, legal requirements, or other factors. We will notify you of material changes by:
            </p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Posting the updated Privacy Policy on this page</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending you an email notification (for significant changes)</li>
              <li>Displaying a prominent notice within the Service</li>
            </ul>
            <p className="mt-2">
              Your continued use of RepConnect after changes are posted constitutes your acceptance
              of the updated Privacy Policy. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">15. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold">RepConnect, LLC</p>
              <p className="mt-1">Privacy Team</p>
              <p>Email: privacy@meetwithreps.com</p>
              <p>Support: support@meetwithreps.com</p>
              <p>Website: meetwithreps.com</p>
            </div>
            <p className="mt-4">
              We take your privacy seriously and will respond to all requests within a reasonable
              timeframe, typically within 30 days.
            </p>
          </section>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              By using RepConnect, you acknowledge that you have read and understood this Privacy
              Policy and agree to the collection, use, and disclosure of your personal information
              as described herein.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyView;
